import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { Icon, type IconName } from './icons.js';

/* ------------------------------------------------------------------ */
/* Buttons                                                             */

export function IconButton({
  icon,
  title,
  onClick,
  active = false,
  disabled = false,
  size = 15,
  className = '',
  shortcut,
}: {
  icon: IconName;
  title: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  size?: number;
  className?: string;
  shortcut?: string;
}) {
  return (
    <button
      type="button"
      className={`ibtn${active ? ' is-active' : ''}${className ? ` ${className}` : ''}`}
      title={shortcut ? `${title} (${shortcut})` : title}
      aria-label={title}
      aria-pressed={active || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Numeric fields                                                      */

function format(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 1000) / 1000;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

/**
 * Number input with a draggable label: drag horizontally on the label to
 * scrub the value (Shift = fine, Ctrl = coarse), type to set it exactly.
 */
export function NumberField({
  value,
  onChange,
  label,
  axis,
  step = 0.1,
  min,
  max,
  integer = false,
  title,
}: {
  value: number | undefined;
  onChange: (value: number) => void;
  label?: string;
  axis?: 'x' | 'y' | 'z' | 'w';
  step?: number;
  min?: number;
  max?: number;
  integer?: boolean;
  title?: string;
}) {
  const current = value ?? 0;
  const shown = value === undefined ? '' : format(value);
  const [text, setText] = useState(shown);
  const [editing, setEditing] = useState(false);
  const scrub = useRef<{ x: number; start: number; moved: boolean } | null>(null);

  useEffect(() => {
    if (!editing) setText(shown);
  }, [shown, editing]);

  const clamp = (v: number) => {
    let n = integer ? Math.round(v) : v;
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    return n;
  };

  function commit(raw: string) {
    setEditing(false);
    const cleaned = raw.trim();
    const parsed = cleaned === '' ? NaN : Number(cleaned);
    if (Number.isFinite(parsed)) {
      const next = clamp(parsed);
      if (next !== current) onChange(next);
      setText(format(next));
    } else {
      setText(shown);
    }
  }

  function onPointerDown(event: ReactPointerEvent<HTMLSpanElement>) {
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    scrub.current = { x: event.clientX, start: current, moved: false };
  }
  function onPointerMove(event: ReactPointerEvent<HTMLSpanElement>) {
    const s = scrub.current;
    if (!s) return;
    const dx = event.clientX - s.x;
    if (Math.abs(dx) > 2) s.moved = true;
    if (!s.moved) return;
    const factor = event.shiftKey ? 0.1 : event.ctrlKey ? 10 : 1;
    onChange(clamp(s.start + dx * step * factor * 0.5));
  }
  function onPointerUp() {
    scrub.current = null;
  }

  return (
    <label className={`numfield${axis ? ` axis-${axis}` : ''}`} title={title}>
      {label !== undefined && (
        <span
          className="numfield-label"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {label}
        </span>
      )}
      <input
        className="numfield-input"
        inputMode="decimal"
        value={text}
        placeholder="—"
        onFocus={(e) => {
          setEditing(true);
          e.currentTarget.select();
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setText(shown);
            setEditing(false);
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
            const next = clamp(current + delta);
            onChange(next);
            setText(format(next));
          }
        }}
      />
    </label>
  );
}

export function VecField({
  value,
  onChange,
  size = 3,
  step = 0.1,
  labels = ['X', 'Y', 'Z', 'W'],
  mixed,
}: {
  value: readonly number[] | undefined;
  /** Second argument: the component that changed. */
  onChange: (value: number[], index: number) => void;
  size?: 2 | 3 | 4;
  step?: number;
  labels?: string[];
  /** Components that differ across a multi-selection (shown as "—"). */
  mixed?: readonly boolean[];
}) {
  const v = Array.from({ length: size }, (_, i) => value?.[i] ?? 0);
  const axes = ['x', 'y', 'z', 'w'] as const;
  return (
    <div className={`vecfield vec-${size}`}>
      {v.map((n, i) => (
        <NumberField
          key={i}
          label={labels[i]}
          axis={axes[i]}
          value={mixed?.[i] ? undefined : n}
          step={step}
          onChange={(next) => {
            const copy = v.slice();
            copy[i] = next;
            onChange(copy, i);
          }}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Text, select, toggle, color, JSON                                   */

export function TextField({
  value,
  onChange,
  placeholder,
  mono = false,
  list,
}: {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  mono?: boolean;
  list?: string;
}) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => setText(value ?? ''), [value]);
  return (
    <input
      className={`textfield${mono ? ' mono' : ''}`}
      value={text}
      placeholder={placeholder}
      list={list}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== (value ?? '')) onChange(text);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setText(value ?? '');
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

export function SelectField({
  value,
  options,
  onChange,
  placeholder,
}: {
  value: string | undefined;
  options: readonly (string | { value: string; label: string })[];
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const normalised = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const known = value === undefined || normalised.some((o) => o.value === value);
  return (
    <div className="selectfield">
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {value === undefined && <option value="">{placeholder ?? '—'}</option>}
        {!known && <option value={value}>{value}</option>}
        {normalised.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="chevronDown" size={12} />
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  mixed = false,
  disabled = false,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  /** Differs across a multi-selection (indeterminate). */
  mixed?: boolean;
  disabled?: boolean;
  /** Accessible name when there is no visible label (e.g. in a table cell). */
  ariaLabel?: string;
}) {
  return (
    <label className={`toggle${mixed ? ' is-mixed' : ''}${disabled ? ' is-disabled' : ''}`}>
      <input
        type="checkbox"
        aria-label={ariaLabel}
        disabled={disabled}
        checked={checked && !mixed}
        ref={(el) => {
          if (el) el.indeterminate = mixed;
        }}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle-track" aria-hidden="true">
        <span className="toggle-thumb" />
      </span>
      {label && <span className="toggle-label">{label}</span>}
    </label>
  );
}

function toHex(c: readonly number[]): string {
  const ch = (v: number | undefined) =>
    Math.round(Math.min(1, Math.max(0, v ?? 0)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(c[0])}${ch(c[1])}${ch(c[2])}`;
}

/** Typed hex color (`#RRGGBB`, `RRGGBB` or `#RGB`), committed on Enter / blur when valid. */
function HexInput({ value, placeholder, onCommit }: { value: string; placeholder?: string; onCommit: (rgb: number[]) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    let hex = draft.trim().replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(hex)) hex = hex.split('').map((ch) => ch + ch).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
      setDraft(value);
      return;
    }
    if (`#${hex.toUpperCase()}` === value) return;
    onCommit([0, 2, 4].map((i) => Math.round((parseInt(hex.slice(i, i + 2), 16) / 255) * 1000) / 1000));
  };
  return (
    <input
      className="colorfield-hex mono"
      value={draft}
      placeholder={placeholder}
      spellCheck={false}
      aria-label="Hex color"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setDraft(value);
        }
      }}
    />
  );
}

/** sRGB color stored as `[r,g,b]` or `[r,g,b,a]` in 0..1. */
export function ColorField({
  value,
  onChange,
  alpha = true,
  mixed = false,
}: {
  value: readonly number[] | undefined;
  onChange: (value: number[]) => void;
  alpha?: boolean;
  /** Differs across a multi-selection. */
  mixed?: boolean;
}) {
  const c = value ?? [1, 1, 1, 1];
  const hasAlpha = alpha && (c.length >= 4 || alpha);
  return (
    <div className="colorfield">
      <label className="colorfield-swatch" style={{ background: toHex(c), opacity: 1 }}>
        <input
          type="color"
          value={toHex(c)}
          onChange={(e) => {
            const hex = e.target.value;
            const rgb = [1, 3, 5].map((i) => Math.round((parseInt(hex.slice(i, i + 2), 16) / 255) * 1000) / 1000);
            onChange(hasAlpha && c.length >= 4 ? [...rgb, c[3] ?? 1] : rgb);
          }}
        />
      </label>
      <HexInput
        value={mixed ? '' : toHex(c).toUpperCase()}
        placeholder={mixed ? '—' : undefined}
        onCommit={(rgb) => onChange(hasAlpha && c.length >= 4 ? [...rgb, c[3] ?? 1] : rgb)}
      />
      {hasAlpha && c.length >= 4 && (
        <NumberField
          label="A"
          value={c[3] ?? 1}
          step={0.05}
          min={0}
          max={1}
          onChange={(a) => onChange([c[0] ?? 1, c[1] ?? 1, c[2] ?? 1, a])}
        />
      )}
    </div>
  );
}

/** Pretty JSON with short numeric arrays kept on one line (`[0, 1.5, 2]`). */
export function compactJson(value: unknown): string {
  return JSON.stringify(value, null, 2).replace(/\[\s+(-?[\d.eE+-]+(?:,\s+-?[\d.eE+-]+)*)\s+\]/g, (_m, inner: string) =>
    `[${inner.split(/,\s+/).join(', ')}]`,
  );
}

/** Free-form JSON editing, committed on blur when it parses. */
export function JsonField({
  value,
  onChange,
  rows = 4,
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  rows?: number;
}) {
  const serialised = value === undefined ? '' : compactJson(value);
  const [text, setText] = useState(serialised);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(serialised);
    setError(null);
  }, [serialised]);
  return (
    <div className="jsonfield">
      <textarea
        className={`mono${error ? ' is-invalid' : ''}`}
        rows={Math.min(14, Math.max(rows, text.split('\n').length))}
        spellCheck={false}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            if (e.target.value.trim()) JSON.parse(e.target.value);
            setError(null);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid JSON');
          }
        }}
        onBlur={() => {
          if (error || text === serialised) return;
          onChange(text.trim() ? JSON.parse(text) : undefined);
        }}
      />
      {error && <span className="jsonfield-error">{error}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Layout helpers                                                      */

export function PropRow({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="prop-row">
      <span className="prop-label" title={hint ?? label}>
        {label}
      </span>
      <div className="prop-control">{children}</div>
    </div>
  );
}

export function Card({
  title,
  icon,
  actions,
  children,
  defaultOpen = true,
  subtitle,
}: {
  title: string;
  icon?: IconName;
  actions?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  subtitle?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`card${open ? ' is-open' : ''}`}>
      <header className="card-head">
        <button type="button" className="card-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={12} />
          {icon && <Icon name={icon} size={14} className="card-icon" />}
          <span className="card-title">{title}</span>
          {subtitle && <span className="card-sub">{subtitle}</span>}
        </button>
        {actions && <div className="card-actions">{actions}</div>}
      </header>
      {open && <div className="card-body">{children}</div>}
    </section>
  );
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon: IconName;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Icon name={icon} size={28} />
      <strong>{title}</strong>
      {children && <div className="empty-state-body">{children}</div>}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

/* ------------------------------------------------------------------ */
/* Menus                                                               */

export interface MenuItem {
  id: string;
  label: string;
  icon?: IconName;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  separator?: boolean;
  /** Section heading (not clickable). */
  heading?: boolean;
  run?: () => void;
}

/** Floating menu at a viewport position; closes on outside click or Escape. */
export function ContextMenu({
  x,
  y,
  items,
  onClose,
}: {
  x: number;
  y: number;
  items: readonly MenuItem[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ x, y });
  // Long menus (Add Component, create lists) get a type-to-filter box.
  const searchable = items.filter((i) => !i.separator && !i.heading).length > 12;
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q
    ? items.filter((i) => !i.separator && !i.heading && i.label.toLowerCase().includes(q))
    : items;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      x: Math.min(x, window.innerWidth - r.width - 6),
      y: Math.min(y, window.innerHeight - r.height - 6),
    });
  }, [x, y]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  return (
    <div className="menu" ref={ref} style={{ left: pos.x, top: pos.y }} role="menu">
      {searchable && (
        <input
          className="menu-search"
          autoFocus
          placeholder="Search…"
          aria-label="Filter menu"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const first = shown.find((i) => !i.separator && !i.heading && !i.disabled);
              if (first) {
                e.preventDefault();
                onClose();
                first.run?.();
              }
            }
          }}
        />
      )}
      {searchable && q && shown.length === 0 && <div className="menu-heading">No matches</div>}
      {shown.map((item) =>
        item.separator ? (
          <div key={item.id} className="menu-sep" role="separator" />
        ) : item.heading ? (
          <div key={item.id} className="menu-heading">
            {item.label}
          </div>
        ) : (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            className={`menu-item${item.danger ? ' is-danger' : ''}`}
            disabled={item.disabled}
            onClick={() => {
              onClose();
              item.run?.();
            }}
          >
            <span className="menu-icon">{item.icon && <Icon name={item.icon} size={14} />}</span>
            <span className="menu-label">{item.label}</span>
            {item.shortcut && <span className="menu-shortcut">{item.shortcut}</span>}
          </button>
        ),
      )}
    </div>
  );
}

/** A button that opens a ContextMenu below itself. */
export function MenuButton({
  items,
  children,
  className = '',
  title,
}: {
  items: readonly MenuItem[] | (() => readonly MenuItem[]);
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  return (
    <>
      <button
        type="button"
        className={className}
        title={title}
        aria-haspopup="menu"
        aria-expanded={at !== null}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setAt(at ? null : { x: r.left, y: r.bottom + 4 });
        }}
      >
        {children}
      </button>
      {at && (
        <ContextMenu
          x={at.x}
          y={at.y}
          items={typeof items === 'function' ? items() : items}
          onClose={() => setAt(null)}
        />
      )}
    </>
  );
}

/** True when keyboard focus is in a text-entry control (shortcuts must yield). */
export function isTyping(target: EventTarget | null = document.activeElement): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const el = target;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'color', 'range'].includes(type);
  }
  // Monaco and xterm use hidden textareas, caught above.
  return Boolean(el.closest('.monaco-editor, .xterm'));
}
